/* eslint-disable no-console */
import { httpModule } from '@shared/http';

// Services
import { Propietor } from '@services/propietor';
import { Tenant } from '@services/tenant';
import { Guarantor } from '@services/guarantor';
import { Billing } from '@services/billing';

// Types
import {
    BaseConceptForBillingPayment,
    ServicesConceptForBillingPayment,
    ContractExpense,
    ContractTaxService,
    ContractDetail
} from '@/assets/entities';

export type ContractPropietor = {
    id: number;
    rate: number;
    propietor: Propietor;
};

export type InterestPaymentFormat = 'Percentage' | 'FixedAmount';
export interface ContractDto {
    tenants?: number[];
    propietors: {
        id: number;
        rate: number;
    }[],
    guarantors?: number[];
    startDate: string;
    property: number;
    operationType: 'Rent' | 'Sale';
    contractType: 'FixedPeriodNewModel' | 'FixedPeriodOldModel' | 'Temporal' | 'Maintenance';
    dueDay?: number;
    possessionDate?: string;
    managementFee?: number;
    managementFeeFormat?: InterestPaymentFormat;
    notes: string;
    calculateInterestSinceFirstDay?: boolean;
    interestRate?: number;
    interestRateFormat?: InterestPaymentFormat;
    duration: number;
    firstMonthToPayService: number;
    details?: {
        amount: number;
        percentageIncrease?: number;
        frequencyOfIncrease?: number;
        incrementBase?: 'FirstMount' | 'LastMonth';
        addBonusConcept?: boolean;
    },
    contractTaxService: ContractTaxService[];
    contractExpenses: ContractExpense[];
};

export type ContractStatus = 'Initiated' | 'Active' | 'To Expire' | 'Rescinded' | 'Finished' | 'To Update';

export interface Contract {
    id: number;
    startDate: string;
    operationType: string;
    contractType: string;
    interestRate: number;
	interestRateFormat: InterestPaymentFormat;
    duration: number;
    endDate: string;
    price: number;
    dueDay: number;
    possessionDate: string;
    notes: string;
    managementFee: number;
	managementFeeFormat: InterestPaymentFormat;
    tenants: Tenant[],
    propietors: ContractPropietor[],
    guarantors: Guarantor[],
    status: ContractStatus;
    property: {
        id: number;
        externalReference: string;
        type: string;
        image: string;
        price: number;
        observations: string;
        address: string;
        notes: string;
        propertyType: string;
        status?: string;
    }
    propietorNames?: string[];
    currentQuota?: null | string;
    calculateInterestSinceFirstDay?: boolean;
    percentageIncrease?: number;
    frequencyOfIncrease?: number;
    incrementBase?: 'FirstMount' | 'LastMonth';
    addBonusConcept?: boolean;
    details?: ContractDetail[];
    passwordSelfmanagement?: string;
    userSelfmanagement?: string;
    mailTenantNotification: boolean;
    mailPropietorNotification: boolean;
    mailAdministrationNotification: boolean;
};

export interface ContractInfo extends Contract {
    billings: Billing[];
    contractTaxService: ContractTaxService[];
    contractExpenses: ContractExpense[];
};

export interface PaymentReceiptInfo {
    id: number;
    paymentMethod: 'POL' | 'Cash' | 'Bank' | 'Transfer' | 'Zenrise Card';
    amountPaid: number;
    paymentDate: string;
    originAccount?: string;
    targetAccount: string;
    observations: string;
    selectedConcepts: (BaseConceptForBillingPayment | ServicesConceptForBillingPayment)[];
};

export interface SettlementReceiptInfo {
        id: number;
        amount: number;
        paymentMethod: 'POL' | 'Cash' | 'Bank' | 'Transfer' | 'Zenrise Card';
        date: string;
        details: {
            name: string;
            amount: number;
            quotes: string;
            imputation: string;
            period: string;
        }[];
};

export interface ContractPeriod {
    billing: Billing;
    paymentReceipt: PaymentReceiptInfo[];
    settlementReceipt: SettlementReceiptInfo[];
};

export interface ContractsFilters {
    properties: number[];
    propietors: number[];
    tenants: number[];
    guarantors: number[];
    statuses: string[];
    durations: number[];
    fromDate: string;
    toDate: string;
}

export const ContractsFiltersDefault = (): ContractsFilters => ({
    properties: [],
    propietors: [],
    tenants: [],
    guarantors: [],
    statuses: [],
    durations: [],
    fromDate: undefined,
    toDate: undefined
});

export interface UpdateStageDTO {
    periodFrom: string;
    periodTo: string;
    contractId: number;
    amount: number;
}

export interface ExtendContractDTO {
    id: number;
    amount: number;
    endDate: string;
}

export type UpdateContractSimpleFields = {
    id: number;
    managementFeeFormat: InterestPaymentFormat;
    interestRateFormat: InterestPaymentFormat;
    managementFee: number;
    interestRate: number;
    dueDay: number;
};

export type UpdatePartsOfContractDto = {
    id: number;
    propietor: number;
    tenant: number;
    guarantors: number[];
    property: number;
}

export type UpdateFullContract = {
    id: number;
    propietor: number;
    tenant: number;
    guarantors: number[];
    property: number;
    managementFee?: number;
    managementFeeFormat?: InterestPaymentFormat;
    interestRate?: number;
    interestRateFormat?: InterestPaymentFormat;
    dueDay: number;
    calculateInterestSinceFirstDay?: boolean;
    frequencyOfIncrease?: number;
    mailTenantNotification?: boolean;
    mailPropietorNotification?: boolean;
    mailAdministrationNotification?: boolean;
}

export async function fetchContracts(page?: number, limit?: number, search?: string, filters?: ContractsFilters) {
    try {
        return await httpModule.get('/contracts', {
            params: {
                page,
                limit,
                search,
                properties: filters?.properties,
                propietors: filters?.propietors,
                tenants: filters?.tenants,
                guarantors: filters?.guarantors,
                statuses: filters?.statuses,
                durations: filters?.durations,
                fromDate: filters?.fromDate,
                toDate: filters?.toDate
            }
        });
    } catch (ex) {
        console.warn('Error fetching contracts');
        console.log(ex.response);
    }
}

// TODO: change any by ContractDTO
export async function postContractByNewModel(contract: any) {
    try {
        return await httpModule.post('/contracts', contract);
    } catch (ex) {
        console.warn('Error posting a contract');
        console.log(ex.response);
    }
}

export async function postContractByOldModel(contract: any) {
    try {
        return await httpModule.post('/contracts/old', contract);
    } catch (ex) {
        console.warn('Error posting a contract');
        console.log(ex.response);
    }
}

export async function searchContract(search: string) {
    try {
        return await httpModule.get(`/contracts?search=${search}`);
    } catch (ex) {
        console.warn('Error while searching a contract');
        console.log(ex.response);
    }
}

export async function importContractToXLSX() {
    try {
        return await httpModule.get('/contracts/import-xlsx');
    } catch (ex) {
        console.warn('Error importing contract from an .XLSX file');
        console.log(ex.response);
    }
}

export async function exportContractToXLSX() {
    try {
        return await httpModule.get('/contracts/export-xlsx');
    } catch (ex) {
        console.warn('Error exporting contract to an .XLSX file');
        console.log(ex.response);
    }
}

export async function getContractBilingsPaidWithDifference() {
    try {
        return await httpModule.get('/contracts/bilings-paid-with-difference');
    } catch (ex) {
        console.warn('Error while getting bilings paid with difference');
        console.log(ex.response);
    }
}

export async function getContractByID(id: number) {
    try {
        return await httpModule.get(`/contracts/${id}`);
    } catch (ex) {
        console.warn('Error while getting a contract');
        console.log(ex.response);
    }
}

export async function getContractToPay(id: number, paymentDate?: string) {
    try {
        return await httpModule.get(`/contracts/contract-to-pay/${id}`, { params: { paymentDate }});
    } catch (ex) {
        console.warn('Error while getting a contract');
        console.log(ex.response);
    }
}


export async function deleteContract(id: number) {
    try {
        return await httpModule.delete(`/contracts/${id}`);
    } catch (ex) {
        console.warn('Error while deleting a contract');
        console.log(ex.response);
    }
}

export async function rescindContract(id: number, date: string) {
    try {
        return await httpModule.post(`/contracts/rescind`, { contract: id, date });
    } catch (ex) {
        console.warn('Error while deleting a contract');
        console.log(ex.response);
    }
}

export async function restoreARescindedContract(id: number) {
    try {
        return await httpModule.post(`/contracts/restore-rescinded-contract`, { contract: id });
    } catch (ex) {
        console.warn('Error while restoring a contract');
        console.log(ex.response);
    }
}

export async function updateContractStage(updateStageDTO: UpdateStageDTO) {
    try {
        return await httpModule.put(`/contracts`, updateStageDTO);
    } catch (ex) {
        console.warn('Error while updating a contract stage');
        console.log(ex.response);
    }
}

export async function getContractReceipt(id: number) {
    try {
        return await httpModule.get(`/contracts/receipt/${id}`);
    } catch (ex) {
        console.warn('Error while getting a contract receipt');
        console.log(ex.response);
    }
}

export async function updateContractSimpleField(updateSimpleFieldsDTO: UpdateContractSimpleFields) {
    try {
        return await httpModule.patch(`/contracts/update-simple-fields`, updateSimpleFieldsDTO);
    } catch (ex) {
        console.warn('Error while updating the contract simple fields');
        console.log(ex.response);
    }
}

export async function updatePartsOfContract(updatePartsOfContractDto: UpdatePartsOfContractDto) {
    try {
        return await httpModule.patch(`/contracts/update-parts-of-contract`, updatePartsOfContractDto);
    } catch (ex) {
        console.warn('Error while updating the contract simple fields');
        console.log(ex.response);
    }
}

export async function updateFullContract(updateFullContract: UpdateFullContract) {
    try {
        return await httpModule.patch(`/contracts/update-full-contract`, updateFullContract);
    } catch (ex) {
        console.warn('Error while updating the contract simple fields');
        console.log(ex.response);
    }
}

export async function extendContract(extendContractDTO: ExtendContractDTO) {
    try {
        return await httpModule.post(`/contracts/extend-contract`, extendContractDTO);
    } catch (ex) {
        console.warn('Error while try to extend a contract');
        console.log(ex.response);
    }
}